import type { GroupBase, StylesConfig } from "react-select";
import client from "libs/client";
import type { ProgramGameSelectOption } from "./types";
import { CLUB_LOAD_DEBOUNCE_MS } from "./constants";

export async function fetchClubSelectOptions(inputValue: string): Promise<ProgramGameSelectOption[]> {
	const res = await client.getDiscoverGamesProgramGameClubOptions.query({
		search: inputValue.trim() || undefined,
		limit: 50,
	});
	const rows = res?.data ?? [];
	return rows.map((row) => ({
		value: typeof row.value === "number" ? row.value : Number(row.value),
		label: String(row.label ?? ""),
	}));
}

export async function fetchTeamSelectOptions(clubId: number): Promise<ProgramGameSelectOption[]> {
	const res = await client.getTeamsByTargetClub.query({ clubId });
	const rows = res?.data ?? [];
	return rows.map((row) => ({
		value: typeof row.value === "number" ? row.value : Number(row.value),
		label: String(row.label ?? ""),
	}));
}

type DebouncedClubLoadRef = {
	timeoutId?: ReturnType<typeof setTimeout>;
	resolve?: (options: ProgramGameSelectOption[]) => void;
};

export function createDebouncedClubLoadOptions(
	debouncerRef: { current: DebouncedClubLoadRef },
): (inputValue: string) => Promise<ProgramGameSelectOption[]> {
	return (inputValue: string) =>
		new Promise((resolve) => {
			const debouncer = debouncerRef.current;
			if (debouncer.timeoutId !== undefined) {
				clearTimeout(debouncer.timeoutId);
				debouncer.resolve?.([]);
			}
			debouncer.resolve = resolve;
			debouncer.timeoutId = setTimeout(async () => {
				debouncer.timeoutId = undefined;
				debouncer.resolve = undefined;
				try {
					resolve(await fetchClubSelectOptions(inputValue));
				} catch {
					resolve([]);
				}
			}, CLUB_LOAD_DEBOUNCE_MS);
		});
}

export function programGameClubAsyncStyles(): StylesConfig<
	ProgramGameSelectOption,
	false,
	GroupBase<ProgramGameSelectOption>
> {
	return {
		container: (provided) => ({
			...provided,
			width: "100%",
		}),
		menuPortal: (base) => ({ ...base, zIndex: 10005 }),
		menu: (provided) => ({
			...provided,
			borderRadius: "5px",
			overflow: "hidden",
			zIndex: 10005,
		}),
		control: (provided) => ({
			...provided,
			borderRadius: "5px",
			cursor: "pointer",
		}),
		placeholder: (provided) => ({
			...provided,
			textTransform: "uppercase",
			fontSize: "12px",
			color: "#000937",
			textWrap: "nowrap",
		}),
		option: (provided, state) => ({
			...provided,
			cursor: "pointer",
			backgroundColor: state.isSelected ? "#000937" : provided.backgroundColor,
			color: state.isSelected ? "#FFFFFF" : provided.color,
			":hover": {
				backgroundColor: "#000937",
				color: "#FFFFFF",
			},
		}),
	};
}
